// Subnet mode, HTML panels per subnet, host grid inside. compact
// labels (last octet only) with the full IP on hover, click opens the
// host drawer. no edges, no concentric layout, nothing that pretends
// to be a network diagram

import React, { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Network, Search } from 'lucide-react'
import type {
  EngagementAsset,
  EngagementAttackRun,
  EngagementEvidence,
  EngagementFinding,
  EngagementPort,
  EngagementSubnet,
} from '@/lib/engagementsApi'
import { aggregate, classifySubnets, RISK_TONES, type HostAggregate, type SubnetAggregate } from './topologyAggregates'

interface Props {
  assets:     EngagementAsset[]
  subnets:    EngagementSubnet[]
  ports:      EngagementPort[]
  attackRuns: EngagementAttackRun[]
  findings:   EngagementFinding[]
  evidence:   EngagementEvidence[]
  onSelect:   (data: Record<string, any> | null) => void
}

export const SubnetGridView: React.FC<Props> = ({ assets, subnets, ports, attackRuns, findings, evidence, onSelect }) => {
  const { subnets: subnetAggs } = useMemo(
    () => aggregate(assets, subnets, ports, attackRuns, findings, evidence),
    [assets, subnets, ports, attackRuns, findings, evidence]
  )
  const { display: displaySubnets, ignored: ignoredSubnets } = useMemo(
    () => classifySubnets(subnetAggs),
    [subnetAggs]
  )
  const [query, setQuery] = useState('')
  const [showIgnored, setShowIgnored] = useState(false)

  const q = query.trim().toLowerCase()
  const applyFilter = (list: SubnetAggregate[]) => list
    .map(s => ({
      ...s,
      hosts: !q ? s.hosts : s.hosts.filter(h =>
        h.asset.host_ip.includes(q) ||
        (h.asset.host_name?.toLowerCase().includes(q) ?? false) ||
        (h.asset.device_type?.toLowerCase().includes(q) ?? false)
      ),
    }))
    .filter(s => !q || s.hosts.length > 0 || s.cidr.includes(q))
  const filtered        = applyFilter(displaySubnets)
  const filteredIgnored = applyFilter(ignoredSubnets)

  return (
    <div className="h-full overflow-auto bg-slate-50 dark:bg-slate-900/40 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
      <div className="flex items-center justify-between mb-3 sticky top-0 bg-slate-50 dark:bg-slate-900/40 z-10 py-1">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
          <Network className="h-3.5 w-3.5" />
          {filtered.length} subnet{filtered.length === 1 ? '' : 's'}
          {ignoredSubnets.length > 0 && (
            <span className="ml-1 text-slate-400 normal-case font-normal" title="Auto-route /32 entries hidden by default">
              · {ignoredSubnets.length} ignored
            </span>
          )}
        </div>
        <label className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Filter hosts…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="pl-7 pr-2 py-1 text-xs border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </label>
      </div>

      {/* constrain the column so on wide monitors subnet panels stay
          readable instead of stretching edge-to-edge */}
      <div className="flex flex-col gap-4 max-w-6xl mx-auto">
        {filtered.map(s => (
          <SubnetPanel key={s.cidr} s={s} onSelect={onSelect} />
        ))}
        {filtered.length === 0 && filteredIgnored.length === 0 && (
          <div className="text-center text-xs text-slate-400 italic py-12">
            {q ? `No hosts match "${query}".` : 'No subnets discovered yet.'}
          </div>
        )}

        {ignoredSubnets.length > 0 && (
          <div className="mt-3 border-t border-slate-200 dark:border-slate-700 pt-3">
            <button
              type="button"
              onClick={() => setShowIgnored(v => !v)}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-400 hover:text-slate-600"
              title="Auto-route /32 entries that don't represent real subnets"
            >
              {showIgnored ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Ignored route artifacts ({ignoredSubnets.length})
            </button>
            {showIgnored && (
              <div className="flex flex-col gap-2 mt-2 opacity-70">
                {filteredIgnored.map(s => (
                  <SubnetPanel key={s.cidr} s={s} onSelect={onSelect} dimmed />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const SubnetPanel: React.FC<{ s: SubnetAggregate; onSelect: (data: Record<string, any> | null) => void; dimmed?: boolean }> = ({ s, onSelect, dimmed }) => {
  const tone = RISK_TONES[s.worstSeverity]
  const borderCls = dimmed ? 'border-dashed border border-slate-300 dark:border-slate-700' : `border-2 ${tone.border}`
  return (
    <section className={`rounded-lg bg-white dark:bg-slate-800 ${borderCls}`}>
      <header className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-700">
        <button
          type="button"
          onClick={() => onSelect(buildSubnetPayload(s))}
          className="text-left flex-1 min-w-0"
        >
          <div className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100">
            {s.cidr}
            {dimmed && <span className="ml-2 text-[9px] uppercase tracking-wide text-slate-400 font-normal">ignored</span>}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {s.hostCount} host{s.hostCount === 1 ? '' : 's'}
            {s.classifiedCount > 0 && <span> · {s.classifiedCount} classified</span>}
            {s.discovery_source && <span className="text-slate-400"> · src: {s.discovery_source}</span>}
          </div>
        </button>
        <div className="flex flex-wrap gap-1.5">
          {s.attackedCount > 0 && <Chip tone="rose">{s.attackedCount} attacked</Chip>}
          {s.highRiskCount > 0 && <Chip tone="orange">{s.highRiskCount} high-risk</Chip>}
          {s.findingCount > 0  && <Chip tone="slate">{s.findingCount} finding{s.findingCount === 1 ? '' : 's'}</Chip>}
        </div>
      </header>

      <div className="p-3">
        {s.hosts.length === 0 ? (
          <div className="text-xs text-slate-400 italic">No hosts in this subnet.</div>
        ) : (
          // cap at 8 columns even on very wide monitors so the grid
          // doesn't stretch into a sparse line on ultra-wide displays
          <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
            {s.hosts.map(h => (
              <HostTile key={h.asset.host_ip} h={h} onClick={() => onSelect(buildHostPayload(h))} />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

const HostTile: React.FC<{ h: HostAggregate; onClick: () => void }> = ({ h, onClick }) => {
  const tone = RISK_TONES[h.topRisk]
  const a = h.asset
  // compact label: last octet, full IP on hover. hostnames clip with
  // truncate so a long FQDN can't blow out the grid column width
  const parts = a.host_ip.split('.')
  const lastOctet = parts[parts.length - 1]
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${a.host_ip}${a.host_name ? ' · ' + a.host_name : ''}${a.device_type ? ' · ' + a.device_type : ''}`}
      className={`text-left bg-white dark:bg-slate-800 ${tone.border} border-2 rounded-md p-2 hover:shadow-md transition-shadow`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100">.{lastOctet}</span>
        <span className={`inline-block w-2 h-2 rounded-full ${tone.dot}`} />
      </div>
      <div className="text-[10px] font-mono text-slate-400 truncate">{a.host_ip}</div>
      {a.host_name && (
        <div className="text-[10px] text-slate-600 dark:text-slate-300 truncate">{a.host_name}</div>
      )}
      {a.device_type && (
        <div className="text-[9px] uppercase tracking-wide text-slate-400 mt-1 truncate">{a.device_type}</div>
      )}
      {(h.attackCount > 0 || h.findings.total > 0) && (
        <div className="flex flex-wrap gap-1 mt-1">
          {h.attackCount > 0   && <MicroChip tone="rose">{h.attackCount}A</MicroChip>}
          {h.findings.total > 0 && <MicroChip tone="slate">{h.findings.total}F</MicroChip>}
        </div>
      )}
    </button>
  )
}

const Chip: React.FC<{ tone: 'rose' | 'orange' | 'slate'; children: React.ReactNode }> = ({ tone, children }) => {
  const map = { rose: 'bg-rose-100 text-rose-700', orange: 'bg-orange-100 text-orange-700', slate: 'bg-slate-100 text-slate-600' }
  return <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded ${map[tone]}`}>{children}</span>
}

const MicroChip: React.FC<{ tone: 'rose' | 'slate'; children: React.ReactNode }> = ({ tone, children }) => {
  const map = { rose: 'bg-rose-100 text-rose-700', slate: 'bg-slate-100 text-slate-600' }
  return <span className={`text-[9px] font-mono font-semibold px-1 py-0 rounded ${map[tone]}`}>{children}</span>
}

function buildSubnetPayload(s: SubnetAggregate) {
  return {
    kind: 'subnet' as const,
    cidr: s.cidr,
    hostCount: s.hostCount,
    attackedCount: s.attackedCount,
    highSevCount: s.highRiskCount,
    mediumSevCount: 0,
    hostIps: s.hosts.map(h => h.asset.host_ip),
  }
}

function buildHostPayload(h: HostAggregate) {
  const a = h.asset
  return {
    type: 'Host',
    ip: a.host_ip,
    hostname: a.host_name,
    mac: a.mac_address,
    vendor: a.vendor,
    subnet: a.subnet_cidr,
    deviceType: a.device_type,
    deviceTypeConfidence: a.device_type_confidence,
    deviceVendor: a.device_vendor,
    deviceModel: a.device_model,
    deviceFirmwareVersion: a.device_firmware_version,
    asset_id: a.id,
  }
}
